/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, Component, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { I18nService, PermissionService, SessionStorageService, TimeService } from '@dm/core';
import { eventType, infigo, InfigoComponent, Utils } from '@dm/live-material-infigo';
import { SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { searchHighHelpEn } from 'assets/global-search/help-high/highHelpEn';
import { searchHighHelpZh } from 'assets/global-search/help-high/highHelpZh';
import { searchMiddleHelpEn } from 'assets/global-search/help-middle/middleHelpEn.js';
import { searchMiddleHelpZh } from 'assets/global-search/help-middle/middleHelpZh.js';
import { isEmpty } from 'lodash';

import { AuthService } from '../../../common/services';
import { IconType } from './global-infigo/const';
import {
    AlarmProvider,
    ConditionalSearchProvider,
    EventProvider,
    HelpProvider,
    OperationProvider,
    PageProvider,
    SourceProvider,
} from './global-infigo/infigo.provider';
import { InfigoService } from './global-infigo/infigo.service';
import { initSuccess } from './global-infigo/infigo.use';
import { GlobalSearchService } from './global-search.service';
import { type SearchData, type SearchEvent, GlobalSearchStrService, GroupTypeE, INCREMENT } from './global-search-str.service';

const { History, Collect } = Utils;

@Component({
    selector: 'app-global-search',
    templateUrl: './global-search.component.html',
    styleUrls: ['./global-search.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
    i18n = this.strService.i18n;
    conditionP: ConditionalSearchProvider;
    @ViewChild('GlobalSearchTemplateRef', { static: true }) sourceTemplateRef: TemplateRef<any>;
    @ViewChild('infigoComponent', { static: true }) infigo: InfigoComponent;
    @ViewChild('ConditionTemplateRef', { static: true }) conditionTemplate: TemplateRef<any>;
    @ViewChild('alarmTemplateRef', { static: true }) alarmTemplateRef: TemplateRef<any>;
    @ViewChild('helpTemplateRef', { static: true }) helpTemplateRef: TemplateRef<any>;
    @ViewChild('loadingTemplateRef', { static: true }) loadingTemplateRef: TemplateRef<any>;
    /**
     * 搜索面板可见性
     */
    visible = false;
    providers = [];
    size = 'small'; // 默认为原本搜索模式
    searchResult: { [key: string]: SearchData[] } = {
        objects: [],
        operations: [],
        pages: [],
    };
    baseCount = INCREMENT;
    iconType = IconType;
    loading = true;
    language = this.i18nService.getLanguage();
    more = false;
    inputValue = '';
    levelIcon = {
        2: '#icon-status-info', // 提示
        3: '#app-icon-alarms-minor-new', // 警告
        5: '#icon-alarms-major',      // 重要
        6: '#icon-alarms-critical',    // 紧急
    };

    SWITCH = SWITCH;

    FeatureConfig = FeatureConfig;

    constructor(
        private strService: GlobalSearchStrService,
        public service: GlobalSearchService,
        private route: ActivatedRoute,
        private router: Router,
        private infigoService: InfigoService,
        public i18nService: I18nService,
        private timeService: TimeService,
        private authService: AuthService,
        public permissionService: PermissionService,
        public sessionStorageService: SessionStorageService,
    ) {
    }

    ngOnInit(): void {
        const userId = this.authService.getUserInfo().userid;
        infigo.history = new History(userId);
        infigo.collect = new Collect(userId);
        infigo.global.convertToStamps = this.timeService.convertToStamps.bind(this.timeService);
        infigo.global.loadingTemplateRef = this.loadingTemplateRef;

        this.service.initI18n().subscribe(
            () => {
                this.loading = false;
            },
            () => {
            },
        );
        this.registerProviders();
        this.registerEvent();
        this.combineKeyup();
    }

    ngOnDestroy(): void {
        window.removeEventListener('keydown', this.keydownShowPanel);
    }

    clearParams(): void {
        const action = this.route.snapshot.queryParams.action;
        const tab = this.route.snapshot.queryParams.tab;
        if (action || tab) {
            this.router.navigate([], {
                queryParams: null,
            });
        }
    }

    /**
     * 注册Provider，每个Provider可以提供一种或多种的搜索能力。
     */
    registerProviders(): void {
        const sourceProvider = new SourceProvider((value, range?) => this.service.findObject(value, range));
        SourceProvider.render = {
            list: this.sourceTemplateRef,
        };
        const pageProvider = new PageProvider([]);
        const operationProvider = new OperationProvider([]);
        const source = this.getSource();
        const helpProvider = new HelpProvider(source);
        ConditionalSearchProvider.render = {
            list: this.conditionTemplate,
        };

        // 与Grammar value保持同步
        const conditionProvider = new ConditionalSearchProvider(
            this.i18nService.language,
            this.infigoService.getSourceBatch,
            this.i18nService,
            this.permissionService.isLicenseExist.bind(this.permissionService));
        this.conditionP = conditionProvider;

        AlarmProvider.render = {
            list: this.alarmTemplateRef,
        };
        EventProvider.render = {
            list: this.alarmTemplateRef,
        };
        HelpProvider.render = {
            list: this.helpTemplateRef,
        };
        const alarmProvider = new AlarmProvider((value, range?) => this.infigoService.getCurrentAlarm(value, range));
        const eventProvider = new EventProvider((value, range?) => this.infigoService.getAllEvent(value, range));
        conditionProvider.extraProvider = [alarmProvider, eventProvider];

        this.service.updateSearchSource.subscribe(({ operation, page }) => {
            pageProvider.update(page);
            operationProvider.update(operation);
        });
        this.providers = [
            pageProvider,
            operationProvider,
            helpProvider,
            sourceProvider,
            conditionProvider,
            alarmProvider,
            eventProvider,
        ];
    }

    /**
     * 获取联机帮助资源
     */
    getSource(): void {
        if (FeatureConfig.IsHighProdHelp) {
            return this.i18nService.language === 'zh' ? searchHighHelpZh : searchHighHelpEn;
        } else {
            return this.i18nService.language === 'zh' ? searchMiddleHelpZh : searchMiddleHelpEn;
        }
    }

    /**
     * 注册事件
     */
    registerEvent(): void {
        infigo.on(eventType.CLICK_LIST_ITEM, (value) => {
            if (value.local) {
                this.jumpToDoc(value);
                return;
            }
            this.clearParams();
            if (value.groupType === GroupTypeE.object) {
                this.sessionStorageService.set('vstoreId', isEmpty(value.vstoreId) ? -1 : value.vstoreId);
            }
            setTimeout(() => {
                this.router.navigate([value.url], {
                    queryParams: {
                        tab: value.tab,
                        subTab: value.subTab,
                        action: value.action,
                        actionParams: value.actionParams,
                    },
                });
            });
            this.infigo.close();
        });
        infigo.on(eventType.SHRINK, (value) => {
            this.inputValue = value;
            this.switchSize();
        });
        infigo.boot = (): void => {
            this.infigoService.checkPmsLunStatus().subscribe(({ selected, pms_status }) => {
                if (selected && pms_status === 0) {
                    infigo.setData(Symbol.for('PMS_LUN_STATUS'), { status: true });
                } else {
                    const abnormal = Boolean(selected && pms_status !== 0);
                    infigo.setData(Symbol.for('PMS_LUN_STATUS'), { status: false, abnormal });
                }
            });
        };
    }

    getTimeString(timeValue: number | string): string {
        return this.timeService.formatUTC(Number(timeValue), 'YYYY-MM-DD HH:mm:ss');
    }

    /**
     * 切换模式
     */
    switchSize = (): void => {
        if (this.isAdvancedMode()) {
            this.size = 'small';
            this.infigo.close();
        } else {
            this.size = 'default';
        }
        this.showSearchPanel();
    };

    hasExpandPermission(): boolean {
        const roleId = (this.permissionService as any).roleId;

        return roleId === 1 || roleId === 2;
    }

    /**
     * 跳转到指定帮助文档
     * @param item 高级搜索中的事件项
     */
    jumpToDoc(item: SearchData): void {
        const baseUrl = this.infigoService.getHelpDocUrl();
        const url = `${baseUrl}#${item.local}`;
        window.open(url, '_blank');
    }

    /**
     * 显示全局搜索panel
     */
    showSearchPanel(): any {
        if (this.isAdvancedMode()) {
            if (initSuccess && !this.infigo.visible) {
                return this.infigo.open();
            }
        } else {
            this.visible = true;
        }

        return undefined as any;
    }

    /**
     * 显示或者关闭全局搜索panel
     */
    toggleSearchPanel(eKey: string): any {
        if (this.isAdvancedMode()) {
            if (eKey === 'Escape' && !this.infigo.visible) {
                return;
            }
            if (initSuccess && this.infigo.visible) {
                this.infigo.close();
            } else {
                this.infigo.open();
            }
        } else {
            if (eKey === 'Escape' && !this.visible) {
                return;
            }
            this.visible = !this.visible;
        }
    }

    /**
     * 是否为高级模式状态
     */
    isAdvancedMode(): boolean {
        return this.size !== 'small' && this.hasExpandPermission();
    }

    /**
     * 呼出全局搜索面板快捷键
     */
    combineKeyup(): void {
        window.addEventListener('keydown', this.keydownShowPanel);
    }

    /**
     * 快捷键展示搜索面板
     */
    keydownShowPanel = (e: KeyboardEvent): void => {
        if (this.isShortcutKeys(e)) {
            e.preventDefault ? e.preventDefault() : e.returnValue = false;
            this.toggleSearchPanel(e.key);
        }
    };

    /**
     * 识别是否在没有遮罩的时候按下ctrl+k
     */
    isShortcutKeys(keys: { key: string; ctrlKey: boolean }): boolean {
        const isCtrlK = (keys.key === 'k' || keys.key === 'K') && keys.ctrlKey || keys.key === 'Escape';
        const noMask = !(document.querySelector('.n-slide-window-mask.show.fade') || document.querySelector('.n-dialog-mask'));
        return isCtrlK && noMask;
    }

    viewPageChange(): void {
        this.visible = false;
    }

    viewMore(): void {
        this.baseCount += INCREMENT;
    }

    searchResultChange(event: SearchEvent): void {
        this.searchResult = { ...event.sourceData };
        this.baseCount = event.baseCount;
        this.more = event.more;
        this.inputValue = event.value;
    }
}
